import React, { useEffect, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSnackbar } from 'notistack';

import { usePrevious } from '@gogame/hooks-ts';
import { Sentry } from '@gogame/components';
import ScreenBody from '../Body';
import { useTitle } from '../Breadcrumbs';
import { DependentEffectProvider } from '../DependentEffect';

import { ScreenModalProviderV2 } from '../Modal';
import { ScreenAISmartSearchProvider } from '../AISmartSearch';
import ScreenToolbar from '../ScreenToolbar';
import { useStorage } from '../Storage';
import ScreenContext from './ScreenContext';
import SateErrorsDialog from './StateErrorsDialog';
import { REINIT } from './constants';
import { useSyncDocumentTitle } from '../DocumentTitle';

const renderProps = (children, props) => {
  return typeof children === 'function'
    ? children(props)
    : React.isValidElement(children)
      ? React.cloneElement(React.Children.only(children), {
          props,
        })
      : null;
};

const Screen = ({ children, name, reducer }) => {
  const {
    state: storageState,
    save,
    errors: storageErrors,
    reset,
    isEqual: isEqualWithStorage,
  } = useStorage();
  const prevStorageState = usePrevious(storageState);

  const { closeSnackbar } = useSnackbar();

  const reactNavigate = useNavigate();

  useEffect(() => {
    window.reactNavigate = reactNavigate;
  }, [reactNavigate]);

  const reducerCallback = React.useCallback(
    (state, action) => {
      if (typeof window.onScreenReducer === 'function') {
        window.onScreenReducer(state, action);
      }
      switch (action.type) {
        case REINIT:
          return action.payload;

        default:
          if (reducer) {
            return {
              data: reducer(state.data, action),
              errors: state.errors,
            };
          }
          return state;
      }
    },
    [reducer]
  );

  const [{ data: screenState, errors }, dispatch] = useReducer(
    reducerCallback,
    {
      data: storageState,
      errors: storageErrors,
    }
  );

  useEffect(() => {
    if (storageState !== screenState && !isEqualWithStorage(screenState)) {
      save(screenState);
    }
  }, [screenState]);

  const { pathname: url } = useLocation();

  const [title, setScreenTitle] = useTitle();

  useEffect(() => {
    if (storageState !== prevStorageState && !isEqualWithStorage(screenState)) {
      dispatch({
        type: REINIT,
        payload: { data: storageState, errors: storageErrors },
      });
    }
  }, [storageState, screenState, storageErrors]);

  useEffect(() => {
    return () => {
      closeSnackbar();
    };
  }, []);

  useSyncDocumentTitle(title);

  useEffect(() => {
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Screen "${name}"`,
      type: 'info',
    });
  }, [name]);

  return (
    <DependentEffectProvider>
      <ScreenModalProviderV2>
        <ScreenContext.Provider
          value={{
            name: name,
            screenState: screenState,
            dispatch: dispatch,
            title: title,
          }}
        >
          <ScreenAISmartSearchProvider>
            {errors.length ? (
              <SateErrorsDialog errors={errors} onUseDefaults={reset} />
            ) : (
              renderProps(children, {
                screenState,
                title,
                setScreenTitle,
                url,
              })
            )}
          </ScreenAISmartSearchProvider>
        </ScreenContext.Provider>
      </ScreenModalProviderV2>
    </DependentEffectProvider>
  );
};

Screen.Toolbar = ScreenToolbar;
Screen.Body = ScreenBody;

export default Screen;
